
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ReportData } from '@/types/report';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Badge } from '@/components/ui/badge';

interface ReportContentSectionProps {
  reportText: string;
  report?: ReportData;
}

export const ReportContentSection: React.FC<ReportContentSectionProps> = ({ reportText, report }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      toast({
        title: t('reports.content.copySuccessTitle'),
        description: t('reports.content.copySuccessDesc'),
      });
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        title: t('reports.content.copyErrorTitle'),
        description: t('reports.content.copyErrorDesc'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold">{t('reports.content.fullReport')}</h3>
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2"
          onClick={handleCopy}
        >
          <Copy size={16} />
          {copied ? t('reports.content.copied') : t('reports.content.copy')}
        </Button>
      </div>
      
      <Textarea
        value={reportText}
        readOnly
        className="min-h-[400px] bg-gray-50"
      />

      {/* Check-in Photos Section */}
      {report?.check_in_photo_urls && report.check_in_photo_urls.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-3">{t('reports.content.checkInPhotos')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {report.check_in_photo_urls.map((photoUrl, index) => (
              <div 
                key={index} 
                className="cursor-pointer border rounded-md overflow-hidden hover:opacity-80 transition-opacity"
                onClick={() => setSelectedImage(photoUrl)}
              >
                <AspectRatio ratio={4/3} className="bg-gray-100">
                  <img 
                    src={photoUrl} 
                    alt={`Check-in photo ${index + 1}`}
                    className="object-cover w-full h-full"
                  />
                </AspectRatio>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Worksheet URLs Section */}
      {report?.worksheet_urls && report.worksheet_urls.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-3">{t('reports.content.worksheets')}</h3>
          <div className="flex flex-wrap gap-2">
            {report.worksheet_urls.map((url, index) => (
              <Badge key={index} variant="outline" className="px-3 py-1">
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline"
                >
                  {t('reports.content.document')} {index + 1} <ExternalLink size={14} />
                </a>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Worksheet Drive Link */}
      {report?.worksheet_drive_link && (
        <div className="mt-4">
          <h3 className="text-lg font-bold mb-3">{t('reports.content.driveLink')}</h3>
          <Badge variant="outline" className="px-3 py-1">
            <a 
              href={report.worksheet_drive_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline"
            >
              {t('reports.content.openDrive')} <ExternalLink size={14} />
            </a>
          </Badge>
        </div>
      )}

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-3xl w-[90vw]">
          <DialogHeader>
            <DialogTitle>{t('reports.content.checkInPhotos')}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="relative w-full">
              <img 
                src={selectedImage} 
                alt="Check-in photo" 
                className="w-full object-contain max-h-[70vh]"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
